export type ExecutionGrantDurationUnit = 'minutes' | 'hours' | 'days';

// Keep this in sync with the backend's EXEC_GRANT_MAX_TTL_MINUTES policy.
export const EXEC_GRANT_MAX_TTL_MINUTES = 43_200;
export const EXEC_GRANT_DEFAULT_DURATION = '60';
export const EXEC_GRANT_MAX_DURATION_LABEL = `${EXEC_GRANT_MAX_TTL_MINUTES / 1440} days`;

const MINUTES_PER_UNIT: Record<ExecutionGrantDurationUnit, bigint> = {
  minutes: 1n,
  hours: 60n,
  days: 1440n
};

export function parseExecutionGrantDuration(
  amount: string,
  unit: ExecutionGrantDurationUnit,
  maxMinutes = EXEC_GRANT_MAX_TTL_MINUTES
): { minutes: number | null; error: string | null } {
  const value = amount.trim();
  if (value.length > 64 || !/^\d+(?:\.\d+)?$/.test(value)) {
    return { minutes: null, error: 'Enter a duration as a positive number.' };
  }

  const [whole, fraction = ''] = value.split('.');
  const scale = 10n ** BigInt(fraction.length);
  const minutesNumerator = (BigInt(whole) * scale + BigInt(fraction || '0')) * MINUTES_PER_UNIT[unit];
  if (minutesNumerator % scale !== 0n) {
    return { minutes: null, error: 'Choose a duration equal to a whole number of minutes.' };
  }

  const minutes = minutesNumerator / scale;
  if (minutes < 1n || minutes > BigInt(maxMinutes)) {
    return {
      minutes: null,
      error: `Duration must be from 1 minute to ${executionGrantMaximumLabel(maxMinutes)}.`
    };
  }
  return { minutes: Number(minutes), error: null };
}

export function executionGrantMaximumLabel(minutes: number): string {
  return minutes === EXEC_GRANT_MAX_TTL_MINUTES ? EXEC_GRANT_MAX_DURATION_LABEL : `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function executionGrantExpiryPreview(minutes: number, now: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    second: '2-digit', timeZoneName: 'short'
  }).format(new Date(now.getTime() + minutes * 60_000));
}
